import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenStorage } from '../services/api';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Send, 
  X, 
  Clock, 
  CheckCircle2, 
  Lock, 
  Plus,
  Lightbulb,
  HelpCircle,
  AlertTriangle,
  MoreHorizontal,
  Trash2,
  Reply,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Kategori tanımları
const CATEGORIES = {
  sarj_sorunu: { 
    label: 'Şarj Sorunu', 
    color: 'bg-red-500', 
    lightColor: 'bg-red-500/10', 
    textColor: 'text-red-500',
    borderColor: 'border-red-500/30',
    icon: AlertTriangle 
  },
  oneri: { 
    label: 'Öneri', 
    color: 'bg-blue-500', 
    lightColor: 'bg-blue-500/10', 
    textColor: 'text-blue-500',
    borderColor: 'border-blue-500/30',
    icon: Lightbulb 
  },
  soru: { 
    label: 'Soru', 
    color: 'bg-purple-500', 
    lightColor: 'bg-purple-500/10', 
    textColor: 'text-purple-500',
    borderColor: 'border-purple-500/30',
    icon: HelpCircle 
  },
  diger: { 
    label: 'Diğer', 
    color: 'bg-gray-500', 
    lightColor: 'bg-gray-500/10', 
    textColor: 'text-gray-500',
    borderColor: 'border-gray-500/30',
    icon: MoreHorizontal 
  }
};

// Rol etiketleri
const ROLE_LABELS = {
  super_admin: 'Sistem Yöneticisi',
  admin: 'Admin',
  hesap_yoneticisi: 'Hesap Yöneticisi',
  site_manager: 'Hesap Yöneticisi',
  site_sakini: 'Site Sakini',
  bireysel_hesap: 'Bireysel Kullanıcı',
  bireysel_yonetici: 'Bireysel Yönetici',
  calisan: 'Çalışan',
  sofor: 'Şoför',
  ziyaretci: 'Ziyaretçi',
  yardimci: 'Yardımcı',
  icerik_yoneticisi: 'İçerik Yöneticisi'
};

// Zaman formatı
const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dk önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  if (diffDays < 7) return `${diffDays} gün önce`;
  return date.toLocaleDateString('tr-TR');
};

// Post Kartı
const PostCard = ({ post, currentUser, onReply, onClose, onDelete }) => {
  const category = CATEGORIES[post.category] || CATEGORIES.diger;
  const CategoryIcon = category.icon;
  const isAdmin = ['super_admin', 'admin', 'hesap_yoneticisi', 'site_manager'].includes(currentUser?.role);
  const isOwner = post.author_id === currentUser?.id;
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setIsSubmitting(true);
    await onReply(post.id, replyText);
    setReplyText('');
    setShowReplyInput(false);
    setIsSubmitting(false);
  };

  return (
    <div className={`bg-card rounded-2xl border ${post.is_closed ? 'border-gray-500/30 opacity-75' : 'border-border'} overflow-hidden transition-all hover:shadow-lg`}>
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
              {post.author_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{post.author_name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {ROLE_LABELS[post.author_role] || post.author_role}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <Clock className="w-3 h-3" />
                <span>{formatTimeAgo(post.created_at)}</span>
              </div>
            </div>
          </div>
          
          {/* Category Badge & Status */}
          <div className="flex items-center gap-2">
            {post.is_closed && (
              <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-500/10 text-gray-400">
                <Lock className="w-3 h-3" />
                Kapatıldı
              </span>
            )}
            <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${category.lightColor} ${category.textColor}`}>
              <CategoryIcon className="w-3 h-3" />
              {category.label}
            </span>
          </div>
        </div>
        
        {/* Content */}
        <p className="mt-3 text-foreground whitespace-pre-wrap">{post.content}</p>
      </div>
      
      {/* Official Reply */}
      {post.official_reply && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-sm font-medium text-green-500">Yönetici Yanıtı</span>
            <span className="text-xs text-muted-foreground">— {post.official_reply.author_name}</span>
          </div>
          <p className="text-sm text-foreground">{post.official_reply.text}</p>
        </div>
      )}
      
      {/* Actions */}
      {(isAdmin || isOwner) && !post.is_closed && (
        <div className="px-4 py-3 border-t border-border flex items-center gap-2">
          {isAdmin && !post.official_reply && (
            <button
              onClick={() => setShowReplyInput(!showReplyInput)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors"
            >
              <Reply className="w-4 h-4" />
              Yanıtla
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => onClose(post.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-gray-500/10 text-gray-400 hover:bg-gray-500/20 transition-colors"
            >
              <Lock className="w-4 h-4" />
              Kapat
            </button>
          )}
          {(isAdmin || isOwner) && (
            <button
              onClick={() => onDelete(post.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </button>
          )}
        </div>
      )}
      
      {/* Reply Input */}
      {showReplyInput && (
        <div className="px-4 pb-4">
          <div className="flex gap-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Resmi yanıtınızı yazın..."
              className="flex-1 px-3 py-2 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              rows={2}
              maxLength={1000}
            />
            <button
              onClick={handleReply}
              disabled={!replyText.trim() || isSubmitting}
              className="px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="text-xs text-muted-foreground mt-1 text-right">{replyText.length}/1000</div>
        </div>
      )}
    </div>
  );
};

// Yeni Post Modal
const NewPostModal = ({ isOpen, onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('diger');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setIsSubmitting(true);
    await onSubmit({ content, category });
    setContent('');
    setCategory('diger');
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-2xl w-full max-w-lg mx-4 shadow-2xl border border-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Yeni Gönderi</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Category Selection */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Kategori</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORIES).map(([key, cat]) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setCategory(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                      category === key
                        ? `${cat.color} text-white`
                        : `${cat.lightColor} ${cat.textColor} hover:opacity-80`
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Content Input */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">İçerik</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, 500))}
              placeholder="Ne paylaşmak istiyorsunuz? Şarj sorunu, öneri veya soru..."
              className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              rows={5}
            />
            <div className={`text-xs mt-1 text-right ${content.length >= 450 ? 'text-orange-500' : 'text-muted-foreground'}`}>
              {content.length}/500
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
            {isSubmitting ? 'Gönderiliyor...' : 'Paylaş'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Ana Sayfa
const BoardPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canPost, setCanPost] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  
  const getToken = () => tokenStorage.get();

  const fetchPosts = async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('No token found');
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      
      const response = await fetch(`${API_URL}/api/board?${params.toString()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
        setCanPost(data.can_post);
      } else {
        console.error('Board fetch failed:', response.status, response.statusText);
        if (response.status !== 401) {
          toast.error('Gönderiler yüklenirken hata oluştu');
        }
      }
    } catch (error) {
      console.error('Board fetch error:', error);
      toast.error('Gönderiler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const handleCreatePost = async ({ content, category }) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/board`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content, category })
      });
      
      if (response.ok) {
        toast.success('Gönderiniz paylaşıldı!');
        fetchPosts();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Gönderi paylaşılamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleReply = async (postId, text) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/board/${postId}/reply`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });
      
      if (response.ok) {
        toast.success('Yanıtınız eklendi');
        fetchPosts();
      } else {
        toast.error('Yanıt eklenemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleClose = async (postId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/board/${postId}/close`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Gönderi kapatıldı');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Bu gönderiyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/board/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Gönderi silindi');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="board-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-primary" />
            Pano
          </h1>
          <p className="text-muted-foreground mt-1">
            Şarj sorunlarınızı, önerilerinizi ve sorularınızı paylaşın
          </p>
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedCategory === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
        >
          Tümü
        </button>
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          return (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === key
                  ? `${cat.color} text-white`
                  : `${cat.lightColor} ${cat.textColor} hover:opacity-80`
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Posts Grid */}
      {posts.length === 0 ? (
        <div className="text-center py-16">
          <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Henüz gönderi yok</h3>
          <p className="text-muted-foreground">İlk gönderiyi siz paylaşın!</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              currentUser={user}
              onReply={handleReply}
              onClose={handleClose}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => canPost ? setShowNewPostModal(true) : null}
        disabled={!canPost}
        className={`fixed bottom-6 right-6 flex items-center gap-2 px-5 py-3 rounded-full shadow-lg transition-all z-40 ${
          canPost
            ? 'bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }`}
        title={canPost ? 'Yeni gönderi paylaş' : 'Bugün paylaşım yaptınız'}
      >
        <Plus className="w-5 h-5" />
        <span className="font-medium">
          {canPost ? 'Yeni Gönderi' : 'Bugün paylaşım yaptınız'}
        </span>
      </button>

      {/* New Post Modal */}
      <NewPostModal
        isOpen={showNewPostModal}
        onClose={() => setShowNewPostModal(false)}
        onSubmit={handleCreatePost}
      />
    </div>
  );
};

export default BoardPage;
